/**
 * CommentSection Component
 * Display comments and add new comments to an OKR
 */

import React, { useState, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Reply, Send } from 'lucide-react';
import { Avatar, Button, EmptyState, LoadingSpinner, MentionTextarea } from '../common';
import { useComments, useCreateComment } from '../../hooks';
import { useAuth } from '../../context/AuthContext';
import type { Comment, MentionUser } from '../../types/comment';

export interface CommentSectionProps {
  okrId: string;
}

export const CommentSection: React.FC<CommentSectionProps> = ({ okrId }) => {
  const { user } = useAuth();
  const { data: comments, isLoading, error } = useComments(okrId);
  const createCommentMutation = useCreateComment();

  const [newComment, setNewComment] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<MentionUser[]>([]);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyMentionedUsers, setReplyMentionedUsers] = useState<MentionUser[]>([]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    await createCommentMutation.mutateAsync({
      okrId,
      content: newComment.trim(),
      mentionedUserIds: mentionedUsers.map(u => u.id)
    });

    setNewComment('');
    setMentionedUsers([]);
  };

  const handleMentionsChange = useCallback((users: MentionUser[]) => {
    setMentionedUsers(users);
  }, []);

  const handleSubmitReply = async (parentCommentId: string) => {
    if (!replyContent.trim()) return;

    await createCommentMutation.mutateAsync({
      okrId,
      content: replyContent.trim(),
      parentCommentId,
      mentionedUserIds: replyMentionedUsers.map(u => u.id)
    });

    setReplyContent('');
    setReplyingTo(null);
    setReplyMentionedUsers([]);
  };

  const handleReplyMentionsChange = useCallback((users: MentionUser[]) => {
    setReplyMentionedUsers(users);
  }, []);

  const handleKeyDown = (
    e: React.KeyboardEvent,
    submitFn: () => void
  ) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submitFn();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4">
        <p className="text-sm text-red-600">Failed to load comments</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        Comments ({comments?.length || 0})
      </h3>

      {/* Add Comment Form */}
      <div className="mb-6">
        <div className="flex gap-3">
          <Avatar
            fallbackText={`${user?.firstName} ${user?.lastName}`}
            size="md"
          />
          <div className="flex-1">
            <MentionTextarea
              value={newComment}
              onChange={setNewComment}
              onMentionsChange={handleMentionsChange}
              placeholder="Add a comment... Use @ to mention someone"
              rows={2}
              onKeyDown={(e) => handleKeyDown(e, handleSubmitComment)}
            />
            <div className="mt-2 flex justify-between items-center">
              <span className="text-xs text-gray-500">
                Type @ to mention someone | Cmd+Enter to submit
              </span>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || createCommentMutation.isPending}
              >
                {createCommentMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-1" />
                    Comment
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Comments List */}
      {!comments || comments.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No comments yet"
          description="Be the first to add a comment to this OKR"
        />
      ) : (
        <div className="space-y-6">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              replyingTo={replyingTo}
              replyContent={replyContent}
              onReplyClick={(id) => {
                setReplyingTo(replyingTo === id ? null : id);
                setReplyContent('');
                setReplyMentionedUsers([]);
              }}
              onReplyContentChange={setReplyContent}
              onReplyMentionsChange={handleReplyMentionsChange}
              onSubmitReply={handleSubmitReply}
              isSubmitting={createCommentMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Sub-component for individual comment
interface CommentItemProps {
  comment: Comment;
  replyingTo: string | null;
  replyContent: string;
  onReplyClick: (id: string) => void;
  onReplyContentChange: (content: string) => void;
  onReplyMentionsChange: (users: MentionUser[]) => void;
  onSubmitReply: (parentId: string) => void;
  isSubmitting: boolean;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  replyingTo,
  replyContent,
  onReplyClick,
  onReplyContentChange,
  onReplyMentionsChange,
  onSubmitReply,
  isSubmitting
}) => {
  const isReplying = replyingTo === comment.id;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmitReply(comment.id);
    }
  };

  return (
    <div className="relative">
      {/* Main Comment */}
      <div className="flex gap-3">
        <Avatar
          fallbackText={`${comment.user.firstName} ${comment.user.lastName}`}
          size="md"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900">
              {comment.user.firstName} {comment.user.lastName}
            </span>
            <time className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
            </time>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {comment.content}
          </p>
          <button
            type="button"
            onClick={() => onReplyClick(comment.id)}
            className="mt-2 inline-flex items-center text-xs text-gray-500 hover:text-primary-600"
          >
            <Reply className="h-3 w-3 mr-1" />
            Reply
          </button>
        </div>
      </div>

      {/* Reply Form */}
      {isReplying && (
        <div className="mt-3 ml-12 flex gap-3">
          <div className="flex-1">
            <MentionTextarea
              value={replyContent}
              onChange={onReplyContentChange}
              onMentionsChange={onReplyMentionsChange}
              placeholder="Write a reply... Use @ to mention someone"
              rows={2}
              autoFocus
              onKeyDown={handleKeyDown}
            />
            <div className="mt-2 flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onReplyClick(comment.id)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onSubmitReply(comment.id)}
                disabled={!replyContent.trim() || isSubmitting}
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Reply'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-4 ml-12 space-y-4 border-l-2 border-gray-100 pl-4">
          {comment.replies.map((reply) => (
            <div key={reply.id} className="flex gap-3">
              <Avatar
                fallbackText={`${reply.user.firstName} ${reply.user.lastName}`}
                size="sm"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900 text-sm">
                    {reply.user.firstName} {reply.user.lastName}
                  </span>
                  <time className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(reply.createdAt), { addSuffix: true })}
                  </time>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {reply.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
